import { navigate } from "gatsby"
import { Fragment, useCallback, useEffect } from "react"

import {
  heyflowClient,
  marketingClient,
  questionnaireClient,
  useQuery,
  useQueryClient,
} from "@trueskin-web/apis"
import { Seo, Spinner } from "@trueskin-web/components"
import { useAuth } from "@trueskin-web/context"
import { sleep } from "@trueskin-web/functions"
import { authService, storageService, vwoService } from "@trueskin-web/services"
import { Routes, useTranslation } from "@trueskin-web/translations"

import ErrorThrow from "../components/error-throw"
import HeyflowWidget from "../components/heyflow-widget"

const provider = "heyflow"
const BVDD_Q_ID = "praxis-questionnaire"

const Questionnaire = () => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const vwoQuestionnaireId = vwoService.getInitialQuestionnaireId()

  const {
    isLoading,
    data: { id, context } = {},
    isError,
  } = useQuery(
    "questionnaireInitial",
    () =>
      questionnaireClient.getInitial({
        id: vwoQuestionnaireId,
        provider,
      }),
    {
      cacheTime: 0,
      useErrorBoundary: true,
    }
  )

  const { isError: isVerifyError } = useQuery(
    "verifyInitial",
    () => questionnaireClient.verifyInitial({ id: vwoQuestionnaireId }),
    { cacheTime: 0, enabled: !!vwoQuestionnaireId }
  )

  const { user } = useAuth()
  const userId = user ? user._id : null

  const onReady = useCallback(() => {
    marketingClient.sendEvent({
      name: marketingClient.EVENT_TYPES_ENUM.INITIAL_Q_FORM_LOADED,
      data: {
        ...context,
        questionnaire_type: provider,
        form_id: id,
      },
    })
  }, [id, context])

  const onChange = useCallback(
    (event) => {
      const eventLabel = event.screenName

      marketingClient.sendEvent({
        name: marketingClient.EVENT_TYPES_ENUM.INITIAL_Q_QUESTION_OPENED,
        data: {
          ...context,
          questionnaire_type: provider,
          form_id: id,
          label: eventLabel,
        },
      })
    },
    [id, context]
  )

  const onSubmit = useCallback(
    async (data) => {
      const { responseId } = data

      if (user?.redoInitialQuestionnaire) {
        marketingClient.trackEvent({
          name: marketingClient.EVENT_TYPES_ENUM
            .RETAKE_INITIAL_QUESTIONNAIRE_SUBMITTED,
          data: {
            ...context,
            questionnaire_type: provider,
            form_id: id,
          },
        })
      } else {
        marketingClient.sendEvent({
          name: marketingClient.EVENT_TYPES_ENUM.INITIAL_Q_SUBMITTED,
          data: {
            ...context,
            questionnaire_type: provider,
            form_id: id,
          },
        })

        marketingClient.sendEvent({
          name: marketingClient.EVENT_TYPES_ENUM.FINISH_INITIAL_QUESTIONNAIRE,
          data: {
            ...context,
            event_id: "INITIAL_Q_" + context.temptoken,
            questionnaire_type: provider,
            form_id: id,
          },
        })
      }

      await heyflowClient.submittedInitial({
        formId: id,
        provider,
        responseId,
        context,
        heyflow: data,
        userId,
      })

      if (user) {
        // HACK: forms api does not update in time if a query is made after a mutation
        await sleep(1500)

        await Promise.all([
          queryClient.refetchQueries("currentUser"),
          queryClient.refetchQueries("initialConsultation"),
        ])
      } else {
        authService.saveTempToken(context.temptoken)
      }

      if (id === BVDD_Q_ID) {
        storageService.setItem({
          id: storageService.getStorageKeys().BVDD_PRAXIS,
          data: true,
        })
      }

      if (!user) {
        navigate(`${Routes.App.Start.CreateAccount.url}/${context.temptoken}`)
      } else {
        navigate(Routes.App.Start.url)
      }

      return null
    },
    [queryClient, user, context, id]
  )

  useEffect(() => {
    document.querySelector("body").style.overflow = "hidden"

    return () => {
      document.querySelector("body").style.overflow = ""
    }
  }, [])

  useEffect(() => {
    if (isVerifyError) {
      vwoService.removeInitialQuestionnaireId()
      navigate(`${Routes.App.Start.url}`)
    }
  }, [isVerifyError])

  return (
    <Fragment>
      <Seo title={t("Start.Questionnaire.seoTitle")} />

      {isLoading ? (
        <Spinner isFullPage />
      ) : isError ? (
        <ErrorThrow message="Unknown form provider" />
      ) : (
        <HeyflowWidget
          id={id}
          urlParameters={context}
          onReady={onReady}
          onQuestionChanged={onChange}
          onSubmit={onSubmit}
        />
      )}
    </Fragment>
  )
}

export default Questionnaire
